import { Accordion, Button } from '@zoom/components';
import classnames from 'classnames';
import { equals } from 'ramda';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AdherenceHistoryFilterSidebarDropdown } from './AdherenceHistoryFilterSidebarDropdown';
import { AdherenceHistoryRadioFilterTile, defaultRadioFilter } from './AdherenceHistoryRadioFilterTile';
import { AdherenceHistoryRangeFilterTile, defaultRangeFilter } from './AdherenceHistoryRangeFilterTile';
import { radioFilterTileOptionsObj } from './config';
import { ACTIVE_SIDEBAR_FILTERS_COUNTER, APPLY_FILTER_BUTTON_TAG } from './constants';
import { AdherenceHistorySidebarFilterState, SidebarFilterTile } from './types';
import { renderRadioTileSubtitleHeader, renderRangeTileSubtitleHeader } from './utils';
import { useAuth } from '../../app/AuthorizationProvider';
import {
  CheckboxFilter,
  CheckboxFilterTile,
  createCheckboxFilterTileSubtitle,
  supervisedTeamsToCheckboxOptionsObj,
} from '../../components/CheckboxFilterTile/CheckboxFilterTile';
import { SidebarBody, SidebarFormWrapper, SidebarHeader } from '../../components/layout/Sidebar';
import { SupervisedTeamsResult } from '../../graphql/types';
import { RangeFilter } from '../../types';

type AdherenceHistorySidebarProps = {
  closeSidebar: () => void;
  preOpenedItemIds: string[];
  setPreOpenedItemIds: React.Dispatch<React.SetStateAction<string[]>>;
  setSidebarFilter: (value: React.SetStateAction<AdherenceHistorySidebarFilterState>) => void;
  sidebarFilter: AdherenceHistorySidebarFilterState;
  teamsResult: SupervisedTeamsResult;
};

export const defaultSidebarFilter: AdherenceHistorySidebarFilterState = {
  adherenceEventsRange: defaultRangeFilter,
  conformanceRange: defaultRangeFilter,
  earlyLeaves: defaultRadioFilter,
  inAdherenceRange: defaultRangeFilter,
  includeInactiveAgents: defaultRadioFilter,
  lateArrivals: defaultRadioFilter,
  teamIds: {},
};

export const AdherenceHistorySidebar: React.FC<AdherenceHistorySidebarProps> = ({
  closeSidebar,
  preOpenedItemIds,
  setPreOpenedItemIds,
  setSidebarFilter,
  sidebarFilter,
  teamsResult,
}) => {
  const { t } = useTranslation();
  const { locale } = useAuth();

  const { teamIds, ...restFilterVariables } = sidebarFilter;

  // Filters states
  const [selectedTeams, setSelectedTeams] = useState<Record<string, CheckboxFilter>>(
    supervisedTeamsToCheckboxOptionsObj(teamsResult, teamIds)
  );

  // Creating arrays[] of assigned teamIds
  const assignedTeamIds = Object.keys(selectedTeams).filter((curr) => selectedTeams[curr].assigned);

  const handleResetAllFilters = (reset: (values: AdherenceHistorySidebarFilterState) => void) => {
    reset(defaultSidebarFilter);
    setSelectedTeams(supervisedTeamsToCheckboxOptionsObj(teamsResult, teamIds, true));

    setPreOpenedItemIds([]);
  };

  const handleSubmit = (values: AdherenceHistorySidebarFilterState) => {
    const checkedTeams: Record<string, CheckboxFilter> = {};

    assignedTeamIds.forEach((assignedTeamId) => {
      checkedTeams[assignedTeamId] = selectedTeams[assignedTeamId];
    });

    setSidebarFilter({
      ...values,
      teamIds: checkedTeams,
    });
    closeSidebar();
  };

  // Generate Filter array for the accordion with subtitle for each header based on current form values in each filter tile
  const getSidebarAdherenceFilters = (
    filterValues: AdherenceHistorySidebarFilterState,
    activeFiltersKeys?: Record<string, boolean>
  ): SidebarFilterTile[] => [
    {
      className: activeFiltersKeys?.teamIds ? 'filter--active' : '',
      dataTestIdPrefix: 'teamIds',
      filterInputType: 'CHECKBOX',
      id: '1',
      items: selectedTeams,
      key: 'teamIds',
      label: t('team.team', { count: 2 }),
      name: t('team.team'),
      setItems: setSelectedTeams,
      subtitle: createCheckboxFilterTileSubtitle(t, selectedTeams, assignedTeamIds),
    },
    {
      className: activeFiltersKeys?.adherenceEventsRange ? 'filter--active' : '',
      filterInputType: 'RANGE',
      id: '2',
      key: 'adherenceEventsRange',
      label: t('scheduleAdherence.outOfAdherenceEvents'),
      subtitle: renderRangeTileSubtitleHeader(filterValues.adherenceEventsRange, t, locale, false),
    },
    {
      className: activeFiltersKeys?.inAdherenceRange ? 'filter--active' : '',
      filterInputType: 'RANGE',
      id: '3',
      isPercentage: true,
      key: 'inAdherenceRange',
      label: t('scheduleAdherence.inAdherence'),
      subtitle: renderRangeTileSubtitleHeader(filterValues.inAdherenceRange, t, locale),
    },
    {
      className: activeFiltersKeys?.conformanceRange ? 'filter--active' : '',
      filterInputType: 'RANGE',
      id: '4',
      isPercentage: true,
      key: 'conformanceRange',
      label: t('scheduleAdherence.conformance'),
      subtitle: renderRangeTileSubtitleHeader(filterValues.conformanceRange, t, locale),
    },
    {
      className: activeFiltersKeys?.lateArrivals ? 'filter--active' : '',
      filterInputType: 'RADIO',
      id: '5',
      key: 'lateArrivals',
      label: t('scheduleAdherence.lateArrivals', { count: 2 }),
      options: radioFilterTileOptionsObj(t).lateArrivals,
      subtitle: renderRadioTileSubtitleHeader('lateArrivals', filterValues.lateArrivals, t),
    },
    {
      className: activeFiltersKeys?.earlyLeaves ? 'filter--active' : '',
      filterInputType: 'RADIO',
      id: '6',
      key: 'earlyLeaves',
      label: t('scheduleAdherence.earlyLeaves', { count: 2 }),
      options: radioFilterTileOptionsObj(t).earlyLeaves,
      subtitle: renderRadioTileSubtitleHeader('earlyLeaves', filterValues.earlyLeaves, t),
    },
    {
      className: activeFiltersKeys?.includeInactiveAgents ? 'filter--active' : '',
      filterInputType: 'RADIO',
      id: '7',
      key: 'includeInactiveAgents',
      label: t('scheduleAdherence.inactivePersons'),
      options: radioFilterTileOptionsObj(t).includeInactiveAgents,
      subtitle: renderRadioTileSubtitleHeader('includeInactiveAgents', filterValues.includeInactiveAgents, t),
    },
  ];

  const getActiveFiltersKeysObj = (values: AdherenceHistorySidebarFilterState) => {
    const activeFiltersKeysObj: Record<string, boolean> = {};

    const filters = getSidebarAdherenceFilters(values);

    filters.forEach((filter) => {
      const filterState = sidebarFilter[filter.key];

      if (filter.filterInputType === 'CHECKBOX') {
        // Creating arrays[] of assigned Ids
        const assignedActiveTeamIds = Object.keys(teamIds).filter((curr) => teamIds[curr].assigned);
        if (assignedActiveTeamIds.length > 0) {
          activeFiltersKeysObj[filter.key] = true;
        }
      }

      if (filter.filterInputType === 'RANGE') {
        const filterCopy = { ...(filterState as RangeFilter) };

        if (typeof (filterCopy?.min || filterCopy?.max) === 'number') {
          activeFiltersKeysObj[filter.key] = true;
        }
      }

      if (typeof filterState === 'string') {
        activeFiltersKeysObj[filter.key] = true;
      }
    });

    return activeFiltersKeysObj;
  };

  const countOfActiveFilters = (values: AdherenceHistorySidebarFilterState) => {
    const activeFiltersKeysObj = getActiveFiltersKeysObj(values);

    return Object.keys(activeFiltersKeysObj).length;
  };

  return (
    <SidebarFormWrapper<AdherenceHistorySidebarFilterState>
      className="sidebar-filter"
      initialValues={restFilterVariables}
      onSubmit={handleSubmit}
    >
      {({ invalid, values, form }) => (
        <>
          <SidebarHeader onClose={closeSidebar}>
            <Button
              data-test-id={APPLY_FILTER_BUTTON_TAG}
              // eslint-disable-next-line no-restricted-syntax -- invalid rule for components from @zoom/components
              disabled={
                invalid ||
                (equals(values, restFilterVariables) &&
                  equals(supervisedTeamsToCheckboxOptionsObj(teamsResult, teamIds), selectedTeams))
              }
              kind="secondary"
              type="submit"
            >
              {t('form.apply')}
            </Button>
            <p
              className={classnames('sidebar-filter__header-wrapper mr-auto', {
                'sidebar-filter__header-wrapper--inactive': !countOfActiveFilters(values),
              })}
              data-test-id={ACTIVE_SIDEBAR_FILTERS_COUNTER}
            >
              <Trans i18nKey="filter.activeFilters" values={{ count: countOfActiveFilters(values) }}>
                {/* space needs to be added within strong tag so the tests can run correctly */}
                <strong>{countOfActiveFilters(values)} </strong>Active filters
              </Trans>
            </p>
            <AdherenceHistoryFilterSidebarDropdown
              isFilterActive={!!countOfActiveFilters(values)}
              resetAllFilters={() => handleResetAllFilters(form.reset)}
              t={t}
            />
          </SidebarHeader>
          <SidebarBody>
            <div className="adherence-history-filters-sidebar__filters-wrapper">
              <Accordion
                className="adherence-history-filters-sidebar__accordion"
                items={getSidebarAdherenceFilters(values, getActiveFiltersKeysObj(values))}
                label={({ label }) => label}
                preOpenedItemIds={preOpenedItemIds}
              >
                {(item) => {
                  if (item.filterInputType === 'CHECKBOX') {
                    return <CheckboxFilterTile filter={item} />;
                  }

                  if (item.filterInputType === 'RANGE') {
                    return <AdherenceHistoryRangeFilterTile filter={item} form={form} />;
                  }

                  if (item.filterInputType === 'RADIO') {
                    return <AdherenceHistoryRadioFilterTile filter={item} form={form} />;
                  }

                  return null;
                }}
              </Accordion>
            </div>
          </SidebarBody>
        </>
      )}
    </SidebarFormWrapper>
  );
};
